// src/hooks/useInvites.js
// Hook quản lý lời mời vào workspace

import { useState, useEffect } from "react";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  arrayUnion,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../contexts/AuthContext";

export function useInvites() {
  const { user } = useAuth();
  // Lời mời đang chờ mình accept
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.email) return;
    // Lắng nghe invites gửi đến email của mình, chưa xử lý
    const q = query(
      collection(db, "invites"),
      where("toEmail", "==", user.email.toLowerCase()),
      where("status", "==", "pending")
    );
    const unsub = onSnapshot(q, (snap) => {
      setPendingInvites(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [user?.email]);

  // Gửi lời mời (owner gọi)
  const sendInvite = async (workspaceId, workspaceName, toEmail) => {
    const email = toEmail.trim().toLowerCase();

    // Kiểm tra không tự mời mình
    if (email === user.email.toLowerCase()) {
      throw new Error("Không thể mời chính mình.");
    }

    // Kiểm tra đã mời email này chưa
    const existing = await getDocs(
      query(
        collection(db, "invites"),
        where("workspaceId", "==", workspaceId),
        where("toEmail", "==", email),
        where("status", "==", "pending")
      )
    );
    if (!existing.empty) {
      throw new Error("Email này đã được mời rồi.");
    }

    await addDoc(collection(db, "invites"), {
      workspaceId,
      workspaceName,
      fromUid: user.uid,
      fromName: user.displayName,
      fromEmail: user.email,
      toEmail: email,
      status: "pending",
      createdAt: serverTimestamp(),
    });
  };

  // Chấp nhận lời mời
  const acceptInvite = async (invite) => {
    if (!user?.uid) return;

    try {
      // 1. Thêm bạn vào danh sách thành viên Workspace
      await updateDoc(doc(db, "workspaces", invite.workspaceId), {
        members: arrayUnion(user.uid),
      });

      // 2. XOÁ LUÔN bản ghi pending này khỏi database
      // Lệnh deleteDoc sẽ làm biến mất bản ghi này ngay lập tức, banner lắng nghe thấy dữ liệu trống sẽ tự động ẩn đi!
      await deleteDoc(doc(db, "invites", invite.id));
      
      console.log("Đã tham gia dự án và dọn dẹp thư mời thành công!");
    } catch (error) {
      console.error("Lỗi khi xử lý acceptInvite:", error);
      throw error;
    }
  };

  // Từ chối lời mời
  const declineInvite = async (inviteId) => {
    await deleteDoc(doc(db, "invites", inviteId));
  };

  return { pendingInvites, loading, sendInvite, acceptInvite, declineInvite };
}

// Hook riêng để lấy danh sách invites đã gửi từ 1 workspace
export function useWorkspaceInvites(workspaceId) {
  const [sentInvites, setSentInvites] = useState([]);

  useEffect(() => {
    if (!workspaceId) return;
    const q = query(
      collection(db, "invites"),
      where("workspaceId", "==", workspaceId),
      where("status", "==", "pending")
    );
    const unsub = onSnapshot(q, (snap) => {
      setSentInvites(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [workspaceId]);

  const cancelInvite = async (inviteId) => {
    await deleteDoc(doc(db, "invites", inviteId));
  };

  return { sentInvites, cancelInvite };
}
